import { useTranslation } from "react-i18next";
import { Banknote, CreditCard, Landmark } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { PAYMENT_METHODS, type PaymentMethod } from "@nh/shared";
import { cn } from "@/shared/lib/cn";

export interface PaymentMethodPickerProps {
  value: PaymentMethod;
  onChange: (method: PaymentMethod) => void;
  /**
   * Qué caminos se ofrecen. La tienda PERSONALIZADA pasa solo
   * transferencia y efectivo (Anahí, 27-ago): ahí no hay Stripe — el
   * dinero va directo al franquiciatario.
   */
  methods?: readonly PaymentMethod[];
}

/**
 * Los tres caminos del dinero (Anahí, 26-ago): tarjeta, transferencia y
 * efectivo. Radios de verdad —agrupados por `name`, con flechas del
 * teclado gratis— vestidos de tarjeta.
 */
const METHODS: Record<
  PaymentMethod,
  { icon: LucideIcon; label: string; description: string }
> = {
  card: {
    icon: CreditCard,
    label: "Tarjeta",
    description: "Pago en línea seguro con Stripe: crédito o débito.",
  },
  transfer: {
    icon: Landmark,
    label: "Transferencia",
    description: "Transferencia bancaria. Conserva tu comprobante: así validamos tu pago.",
  },
  cash: {
    icon: Banknote,
    label: "Efectivo",
    description: "Pagas al coordinar la entrega de tu pedido.",
  },
};

export function PaymentMethodPicker({
  value,
  onChange,
  methods = PAYMENT_METHODS,
}: PaymentMethodPickerProps) {
  const { t } = useTranslation();

  return (
    <fieldset className="grid gap-3">
      <legend className="sr-only">{t("Método de pago")}</legend>
      {methods.map((method) => {
        const { icon: Icon, label, description } = METHODS[method];
        const active = value === method;
        return (
          <label
            key={method}
            className={cn(
              "flex cursor-pointer items-start gap-4 rounded-2xl border p-4 transition",
              active
                ? "border-ds-violet-pop bg-ds-lavender-bg-2/60 ring-1 ring-ds-violet-pop"
                : "border-ds-border bg-white hover:border-ds-ink-3/40",
            )}
          >
            <input
              type="radio"
              name="paymentMethod"
              value={method}
              checked={active}
              onChange={() => onChange(method)}
              className="mt-1 h-4 w-4 accent-ds-violet-pop"
            />
            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                active ? "bg-ds-violet-pop text-white" : "bg-ds-mist text-ds-ink-3",
              )}
            >
              <Icon className="h-5 w-5" />
            </span>
            <span className="grid gap-0.5">
              <span className="text-[15px] font-semibold text-ds-ink">{t(label)}</span>
              <span className="text-[13px] leading-relaxed text-ds-ink-3">
                {t(description)}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
